import "ka-shared-readonly/pluginMomentDateNumber";
import "react-dates/initialize";
import { I18nextProvider } from "react-i18next";
import { isMobile } from "components/ReactUtils/mobileDetect.js";
import { Provider } from "mobx-react";
import { Router, Redirect } from "@reach/router";
import { ThemeProvider } from "emotion-theming";
import Component from "@reach/component-component";
import Helmet from "react-helmet";
import i18next from "translations/i18n";
import React, { Suspense, lazy, useEffect } from "react";
import styled from "react-emotion";
import theme from "components/theme";
import "translations/global";

import "react-virtualized/styles.css"; // only needs to be imported once
import "react-dates/lib/css/_datepicker.css";
import "../../styles/app.css";

import ApiProvider from "../Auth/ApiProvider";

import PageAuthLogin from "../Auth/PageAuthLogin";
import { RecoilRoot } from "recoil";

const KitchenStudiosStoreRatingPage = lazy(() => import("components/KitchenStudiosStoreRatingPage"));
const LeadForm = lazy(() => import("components/LeadForm"));

const FeedbackForm = lazy(() => import("components/FeedbackForm"));
const PageConsole = lazy(() => import("components/Console/Layout"));

const PageCalendar = lazy(() => import("components/PageCalendar"));
const MyKitchenThankYou = lazy(() => import("components/MyKitchenThankYou"));

const LeadFormIFrame = lazy(() => import("components/LeadFormIFrame"));
const MyKitchen = lazy(() => import("components/MyKitchen"));
const PageKitchenGuide = lazy(() => import("components/PageKitchenGuide"));
const PageStore = lazy(() => import("components/PageStore"));
const PageStoreKitchen = lazy(() => import("components/StoreKitchen/PageStoreKitchen"));
const FloorPlanner = lazy(() => import("components/FloorPlanner"));
const AppliancesShopRoot = lazy(() => import("../AppliancesShop/AppliancesShopRoot"));
const LuxonaPage = lazy(() => import("../LuxonaPage/App"));
const LeadFormLux = lazy(() => import("../LeadForm/LeadFormLux"));
const LeadFormLuxCalendarPage = lazy(() => import("../LeadForm/LeadFormLuxCalendarPage"));
const PageCareers = lazy(() => import("components/PageCareers"));
const PageCareerDetails = lazy(() => import("components/PageCareerDetails"));

const PageEmailSubscriptionDeactivate = lazy(() => import("components/PageEmailSubscriptionDeactivate"));
const PageEmailSubscriptionReactivate = lazy(() => import("components/PageEmailSubscriptionReactivate"));

const ConsoleCrawlerStoresScreen = lazy(() => import("../../components/Console/CrawlerStores/Screen"));
const ConsoleEmailTemplates = lazy(() => import("../../components/ConsoleEmailTemplates"));

require("./base.module.css");

function NotFound() {
  useEffect(() => {
    // window.location.pathname = "/404";
  }, []);

  return null;
}

function HelmetForLanguage({ lng }) {
  return (
    <Helmet path="/">
      <link rel="alternate" href={window.location.href} hrefLang={lng} />
    </Helmet>
  );
}

class Root extends React.Component {
  constructor() {
    super();

    i18next.changeLanguage("de");
  }

  renderLuxona() {
    return (
      <Suspense fallback={<div></div>}>
        <div className={this.props.className}>
          <Helmet>
            <html data-touch={isMobile() ? "touch" : "no-touch"} />
            <link rel="shortcut icon" href="favicon-luxona.png" />
            <title>Luxona Küchen - Der Küchen-Concierge für Ihre Premium Küche</title>
            <meta
              name="description"
              content="Finden Sie Ihre Traumküche mit dem einzigartigen Luxona Küchen Concierge Service. Ein Berater steht Ihnen während des kompletten Kauf-Prozesses zur Seite und berät Sie objektiv und unabhängig, welche Küche am besten zu Ihnen passt. Jetzt ausprobieren!"
            />
          </Helmet>
          <Provider>
            <ThemeProvider theme={theme}>
              <ApiProvider>
                <I18nextProvider i18n={i18next}>
                  <div>
                    <Router>
                      <LuxonaPage path="/" />
                      <LeadFormLux path="/schritt/:step" />
                      <LeadFormLuxCalendarPage path="/:leadId/kalender" />
                    </Router>
                  </div>
                </I18nextProvider>
              </ApiProvider>
            </ThemeProvider>
          </Provider>
        </div>
      </Suspense>
    );
  }

  render() {
    const isLuxona = window.location.href.match(/luxona/);

    if (isLuxona) {
      return this.renderLuxona();
    }

    return (
      <Suspense fallback={<div></div>}>
        <div className={this.props.className}>
          <Helmet>
            <html data-touch={isMobile() ? "touch" : "no-touch"} />
            <link rel="shortcut icon" href="favicon.ico" />
          </Helmet>
          <Provider>
            <RecoilRoot>
              <ThemeProvider theme={theme}>
                <ApiProvider>
                  <I18nextProvider i18n={i18next}>
                    <div>
                      <Router>
                        <Component
                          getInitialState={() => {
                            i18next.changeLanguage("en");
                            return {};
                          }}
                          path="/en/"
                        >
                          <LeadForm path="schritt/:step" />
                          <MyKitchen path="mykitchen/:leadSlug/*" />

                          <MyKitchenThankYou
                            path="danke-mit-grundriss/:leadId"
                            triggerPageView
                            page="floorplan"
                            step={2}
                          />
                          <MyKitchenThankYou
                            path="danke-ohne-grundriss/:leadId"
                            triggerPageView
                            page="noFloorplan"
                            step={2}
                          />
                          <FeedbackForm path="mykitchen/:leadSlug/:storeNumber/feedback" />

                          <PageConsole path="console/*" />
                        </Component>

                        <KitchenStudiosStoreRatingPage path="/kuechenstudios/:profileSlug/rating/" />

                        <PageAuthLogin path="/login/*" />
                        <PageCalendar path="/kalender/:leadId" />
                        <PageConsole path="/console/*" />
                        <ConsoleCrawlerStoresScreen path="/console/crawler-stores" />
                        <ConsoleEmailTemplates path="/console/email-templates/*" />
                        <PageEmailSubscriptionDeactivate path="/:docId/unsubscribe" />
                        <PageEmailSubscriptionReactivate path="/:docId/unsubscribe/thankyou" />
                        <PageKitchenGuide path="/ka/*" />

                        <AppliancesShopRoot path="/stores/:storeSlug/shop/*" />
                        <PageStore path="/stores/:storeSlug/*" />
                        <PageStoreKitchen path="/storekitchen/:storeSlug/:leadSlug/*" />

                        <LeadForm path="/schritt/:step" />
                        <LeadFormIFrame path="/iframe/funnel/:step" />
                        <FeedbackForm path="/mykitchen/:leadSlug/:storeNumber/feedback" />

                        <MyKitchen path="/mykitchen/:leadSlug/*" />
                        <MyKitchenThankYou path="danke-partner" page="floorplan" step={2} />
                        <MyKitchenThankYou
                          path="/danke-mit-grundriss/:leadId"
                          triggerPageView
                          page="floorplan"
                          step={2}
                        />
                        <MyKitchenThankYou
                          path="/danke-ohne-grundriss/:leadId"
                          triggerPageView
                          page="noFloorplan"
                          step={2}
                        />

                        <FloorPlanner path="/floorplanner" />

                        <Redirect from="/mykitchen/:leadSlug/feedback/" to="/mykitchen/:leadSlug" />
                        <Redirect from="/storeoverview/:storeSlug" to="/stores/:storeSlug/overview" />
                        <Redirect from="/storekitchen/:storeSlug" to="/stores/:storeSlug/console" />

                        <PageCareers path="/karriere/" />
                        <PageCareerDetails path="/karriere/:id" />

                        <NotFound default />
                      </Router>
                      <Router primary={false}>
                        <HelmetForLanguage lng="de" path="/*" />
                        <HelmetForLanguage lng="en" path="/en/*" />
                      </Router>
                    </div>
                  </I18nextProvider>
                </ApiProvider>
              </ThemeProvider>
            </RecoilRoot>
          </Provider>
        </div>
      </Suspense>
    );
  }
}

Root = styled(Root)`
  .gButtonLikeLink {
    color: #555;
    background: none;
    border: none;
    margin: 0;
    padding: 0;

    &:hover {
      cursor: pointer;
      text-decoration: underline;
    }
  }

  .container2 {
    padding-right: 15px;
    padding-left: 15px;
    margin-right: auto;
    margin-left: auto;
    width: 98%;
  }

  @media (min-width: 1410px) {
    .container2 {
      width: 1400px;
    }
  }
`;

export default Root;
